import { useCallback, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import type { ViewToken } from 'react-native';
import { ColorValues, Flex, List } from '@banhala/ui-kit';
import type { MarketCarouselEntity, MarketCarouselProps } from './types';
import { TwoImageHeader } from './components/TwoImageHeader';
import { MarketCard } from './components/MarketCard';
import { FavoriteButton } from './components/FavoriteButton';
import { bridge, isNil } from '../../../../utils';

const CARD_WIDTH = 202;
const INFO_HEIGHT = 68;

const VIEWABILITY_CONFIG = { itemVisiblePercentThreshold: 50 };

const keyExtractor = (_item: MarketCarouselEntity, index: number) =>
  index.toString();

const TwoImageCard = ({ item, logging }: MarketCarouselEntity) => {
  const { sno, profile, name, description, goods_images, favorite } = item;

  const handlePressCard = useCallback(() => {
    bridge.logAnalyticsEvent({
      name: 'CLICK_MARKET',
      analytics: logging.analytics,
      ably_only_log: { ...logging.ably_only_log, event_name: 'click' },
    });
    bridge.openDeeplink(`ably://markets/${sno}/`);
  }, [sno, logging]);

  return (
    <View style={styles.cardPadding}>
      <View style={styles.cardShadow}>
        <Flex width={CARD_WIDTH} overflow="hidden" onPress={handlePressCard}>
          <TwoImageHeader images={goods_images} cardWidth={CARD_WIDTH} />

          <MarketCard
            sno={sno}
            profile={profile}
            name={name}
            description={description}
            logging={logging}
            profileSize={44}
            nameType="subtitle2"
            descriptionType="body4"
            descriptionColor="content_tertiary"
            height={INFO_HEIGHT}
            paddingHorizontal={8}
            paddingVertical={12}
            textGap={4}
            pressable={false}
            showChevron={true}
          />

          <View style={styles.favoriteContainer}>
            <FavoriteButton
              sno={sno}
              isFavorited={favorite?.is_favorited ?? false}
              logging={logging}
              uncheckedColor={ColorValues.white}
            />
          </View>
        </Flex>
      </View>
    </View>
  );
};

const renderItem = ({ item }: { item: MarketCarouselEntity }) => (
  <TwoImageCard item={item.item} logging={item.logging} />
);

export const TwoImageMarketCarousel = ({ entity }: MarketCarouselProps) => {
  const displayedRef = useRef(new Set<number>());

  const onViewableItemsChanged = useCallback(
    ({
      viewableItems,
    }: {
      viewableItems: ViewToken<MarketCarouselEntity>[];
    }) => {
      viewableItems.forEach(({ index, isViewable, item }) => {
        if (index === null || !isViewable || isNil(item)) return;
        if (displayedRef.current.has(index)) return;
        displayedRef.current.add(index);
        bridge.logAnalyticsEvent({
          name: 'DISPLAY_MARKET',
          analytics: {
            ...item.logging.analytics,
            IS_FAVORITED: item.item.favorite?.is_favorited ? 1 : 0,
          },
          ably_only_log: item.logging.ably_only_log,
        });
      });
    },
    [],
  );

  if (isNil(entity) || entity.length === 0) {
    return null;
  }

  return (
    <List
      horizontal
      data={entity}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.scrollContent}
      onViewableItemsChanged={onViewableItemsChanged}
      viewabilityConfig={VIEWABILITY_CONFIG}
    />
  );
};

const styles = StyleSheet.create({
  scrollContent: {
    paddingHorizontal: 12,
    paddingBottom: 24,
    gap: 8,
  },
  cardPadding: {
    padding: 1,
  },
  cardShadow: {
    backgroundColor: ColorValues.white,
    elevation: 1,
    shadowColor: ColorValues.black,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 1,
  },
  favoriteContainer: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
});
